// covered-by: frontends/carrie/src/ask-v2/review/__tests__/AskV2ReviewCommentsList.test.tsx
import { ArrowClockwise, CheckCircle, XCircle } from "@phosphor-icons/react";
import { Button, Flex } from "@radix-ui/themes";
import { useTranslation } from "react-i18next";

type JudgmentTone = "confirm" | "reject" | "restore";

function JudgmentActionButton({
  "data-testid": dataTestId,
  label,
  icon: Icon,
  pressed,
  onClick,
  tone,
  disabled,
}: {
  "data-testid": string;
  label: string;
  icon: typeof CheckCircle;
  pressed: boolean;
  onClick: () => void;
  tone: JudgmentTone;
  disabled?: boolean;
}) {
  const { t } = useTranslation("ui");
  const isConfirm = tone === "confirm";
  const isRestore = tone === "restore";
  const variant = isConfirm && pressed ? "solid" : pressed ? "soft" : "outline";

  return (
    <Button
      type="button"
      variant={variant}
      size="1"
      color={isConfirm ? "green" : "gray"}
      data-testid={dataTestId}
      aria-label={
        pressed
          ? t("assetViewer.removeLabel", { label: label.toLowerCase() })
          : label
      }
      aria-pressed={isRestore ? undefined : pressed}
      onClick={onClick}
      disabled={disabled}
      className="min-w-0 flex-1 basis-0"
    >
      <Flex as="span" gap="1" align="center" justify="center">
        <Icon
          size={14}
          weight={isConfirm && pressed ? "fill" : "regular"}
          aria-hidden="true"
        />
        {label}
      </Flex>
    </Button>
  );
}

export type AskV2ReviewCommentJudgmentActionsProps = {
  commentId: string;
  liked?: boolean | null;
  includeRejected: boolean;
  disabled?: boolean;
  onJudge: (liked: boolean | null) => void;
};

export function AskV2ReviewCommentJudgmentActions({
  commentId,
  liked = null,
  includeRejected,
  disabled = false,
  onJudge,
}: AskV2ReviewCommentJudgmentActionsProps) {
  const { t } = useTranslation("ui");
  const isRejected = liked === false;
  const showRestore = isRejected && includeRejected;

  return (
    <Flex
      gap="2"
      className="mt-3 border-t border-puntt-neutral-gray-5 pt-3"
      data-testid={`ask-v2-review-comment-judgment-${commentId}`}
    >
      {showRestore ? (
        <JudgmentActionButton
          data-testid={`ask-v2-review-comment-restore-${commentId}`}
          label={t("assetViewer.restore")}
          icon={ArrowClockwise}
          pressed={false}
          onClick={() => onJudge(null)}
          tone="restore"
          disabled={disabled}
        />
      ) : (
        <>
          <JudgmentActionButton
            data-testid={`ask-v2-review-comment-confirm-${commentId}`}
            label={t("assetViewer.confirm")}
            icon={CheckCircle}
            pressed={liked === true}
            // why: null clears judgment; false would reject-and-hide via liked !== false
            onClick={() => onJudge(liked === true ? null : true)}
            tone="confirm"
            disabled={disabled}
          />
          <JudgmentActionButton
            data-testid={`ask-v2-review-comment-reject-${commentId}`}
            label={t("assetViewer.reject")}
            icon={XCircle}
            pressed={false}
            onClick={() => onJudge(false)}
            tone="reject"
            disabled={disabled}
          />
        </>
      )}
    </Flex>
  );
}
